<script setup lang="ts">
import client from "@/utils/axios";
import { defineProps, defineEmits, reactive, ref } from "vue";

type Props = {
  selectedInput: any;
  projectId: string | null | undefined;
};

type Emits = {
  (event: "toggleModal"): void;
  (event: "addTechnicalData", newData: any): void;
  (event: "refresh"): void;
};

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const files = ref([]);

const inputTitle = ref(props.selectedInput ? props.selectedInput.value : "");

const imagePreviews = ref<string[]>([]);

const handleFileChange = (event: any) => {
  files.value = Array.from(event.target.files);
  imagePreviews.value = files.value.map((file: any) =>
    URL.createObjectURL(file)
  );
  errors.imageUpload = "";
};

const errors = reactive({
  inputTitle: "",
  imageUpload: "",
});

const validate = () => {
  let valid = true;
  errors.inputTitle = "";

  if (!inputTitle.value) {
    valid = false;
    errors.inputTitle = "This field is required!";
  } else if (inputTitle.value.toString().length > 48) {
    valid = false;
    errors.inputTitle = "Max 48 characters.";
  }
  if (!props.selectedInput.image) {
    if (files.value.length === 0) {
      valid = false;
      errors.imageUpload = "Image is required!";
    } else {
      valid = true;
      errors.imageUpload = "";
    }
  }

  return valid;
};

const onSubmit = async () => {
  if (!validate()) return;
  try {
    const formData = new FormData();
    formData.append("name", inputTitle.value);
    formData.append("order", props.selectedInput.order);

    files.value.forEach((file, index) => {
      formData.append(`file[${index}]`, file);
    });

    formData.append(
      "group",
      props.selectedInput.id ? props.selectedInput.id : -1
    );
    await client.post(
      `/projects/${props.projectId}/save-group-details`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    inputTitle.value = "";
    emits("toggleModal");
    emits("refresh");
  } catch (error) {
    console.error("Error:", error);
  }
};
</script>

<template>
  <div
    class="fixed top-0 right-0 bottom-0 left-0 w-full h-screen flex items-center justify-center bg-black/40 z-[200]"
  >
    <div
      class="w-[628px] -translate-y-0 p-5 rounded-primary-radius bg-[#FFFFFF]"
    >
      <!-- header -->
      <div
        class="flex items-center justify-between pb-[16px] border-b border-[#E3E1EA]"
      >
        <!-- left side -->
        <h3
          class="text-primary-text-color text-xl leading-[32px] font-semibold tracking-[0.01rem]"
        >
          Bauabschnitt
        </h3>
        <!-- close button -->
        <img
          @click="$emit('toggleModal')"
          class="cursor-pointer"
          src="@/assets/close-icon-2.svg"
          alt="Close icon"
        />
      </div>
      <!-- Content -->
      <div>
        <!-- Input -->
        <div
          class="mt-4"
          :class="imagePreviews && imagePreviews.length ? 'mb-5' : 'mb-0'"
        >
          <h4
            class="pb-[6px] text-base leading-[26px] tracking-[0.01rem] font-medium text-[#898790]"
          >
            Title
          </h4>
          <input
            v-model="inputTitle"
            type="text"
            @input="errors.inputTitle = ''"
            placeholder="Placeholder"
            class="w-full p-3 rounded-primary-radius outline-none text-base text-primary-text-color font-normal tracking-[0.01rem] bg-transparent placeholder:text-[#898790]"
            :class="
              !errors.inputTitle
                ? 'border border-[#E3E1EA]'
                : 'border border-[#ED857F]'
            "
          />
          <p v-if="errors.inputTitle" class="text-[#ED857F] text-sm pt-1">
            {{ errors.inputTitle }}
          </p>
        </div>
        <!-- Upload file -->
        <div
          v-if="imagePreviews.length"
          class="flex w-full overflow-x-auto gap-1 mt-6 whitespace-nowrap scrollable-container"
        >
          <div
            v-for="(src, index) in imagePreviews"
            :key="index"
            class="shrink-0 w-[123px] h-[123px]"
          >
            <img
              :src="src"
              alt="Uploaded image"
              class="shrink-0 object-cover w-[123px] h-[123px] rounded-md"
            />
          </div>
        </div>
        <div
          v-else-if="selectedInput && selectedInput.image"
          class="flex w-full overflow-x-auto gap-1 mt-6 whitespace-nowrap scrollable-container"
        >
          <div class="shrink-0 w-[123px] h-[123px]">
            <img
              :src="selectedInput?.image"
              alt="Uploaded image"
              class="shrink-0 object-cover w-[123px] h-[123px] rounded-md"
            />
          </div>
        </div>
        <div class="mt-[24px] mb-5">
          <label
            for="file"
            class="flex items-center gap-2 px-4 py-2 text-[#393840] font-medium rounded-md cursor-pointer transition border-2 border-[#393840] w-fit hover:bg-[#F9F8F6]"
          >
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.40186 7.59852L6.57257 2.4278C8.11026 0.890114 10.6034 0.890114 12.1411 2.4278C13.6788 3.96549 13.6786 6.45872 12.1409 7.99641L6.17468 13.9626C5.14956 14.9877 3.48778 14.9876 2.46265 13.9625C1.43753 12.9373 1.43728 11.2755 2.4624 10.2503L8.42862 4.28413C8.94118 3.77156 9.77265 3.77156 10.2852 4.28413C10.7978 4.79669 10.7974 5.62752 10.2849 6.14008L5.11414 11.3108"
                stroke="#393840"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Attach Image
          </label>
          <input
            multiple
            class="hidden"
            type="file"
            id="file"
            @change="handleFileChange"
          />
          <span class="text-sm text-[#ED857F] mt-1" v-if="errors.imageUpload">{{
            errors.imageUpload
          }}</span>
        </div>
      </div>
      <!-- Footer -->
      <div
        class="w-full flex items-center justify-end border-t border-black/10 pt-4"
      >
        <button
          @click="onSubmit"
          class="w-[150px] bg-black rounded-primary-radius text-white py-3 text-base leading-[26px] tracking-[0.01rem] font-semibold"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</template>
